import React, { useEffect } from 'react'
import useServerSent from './useServerSent'
import { SSEData, GameEventUpdate } from './types'
import { Container, Header, ScoreBoard, PlayLogItem, Controls, Button } from './styles'
import { PlayerGameStats } from '../../Api/players/players.types'
import { TeamGameStats } from '../../Api/teams/teams.types'
import { PlayLog } from '../../Api/events/events.types'

interface SSEControlledProps {
  url: string
  headers?: Record<string, string>
}

const validateScoreboard = (
  scoreboard: unknown
): scoreboard is GameEventUpdate['scoreboard'] => {
  if (!scoreboard || typeof scoreboard !== 'object') {
    console.warn('Invalid scoreboard data: not an object', scoreboard)
    return false
  }

  const requiredFields = [
    'homeScore',
    'awayScore',
    'homePeriodScores',
    'awayPeriodScores',
    'period',
    'minutes',
    'seconds',
  ]

  const missingFields = requiredFields.filter(
    (field) => !(field in (scoreboard as object))
  )

  if (missingFields.length > 0) {
    console.warn('Incomplete scoreboard data: missing fields', missingFields)
    return false
  }

  return true
}

const SSEComponent: React.FC<SSEControlledProps> = ({ url, headers }) => {
  const { data, error, isConnected, connect, disconnect } = useServerSent<SSEData>({
    url,
    headers,
  })

  useEffect(() => {
    if (data?.type === 'eventUpdate' && typeof data.data !== 'string') {
      const gameData = data.data

      try {
        // Log player statistics if available
        console.log('Game Data:', gameData)
        if (Array.isArray(gameData.playerStats)) {
          console.log('Player Statistics:', {
            totalPlayers: gameData.playerStats.length,
            byTeam: gameData.playerStats.reduce((acc, player) => {
              if (player?.teamId) {
                if (!acc[player.teamId]) {
                  acc[player.teamId] = []
                }
                acc[player.teamId].push(player)
              }
              return acc
            }, {} as Record<string, PlayerGameStats[]>),
          })
        } else {
          console.warn('Player statistics not available or invalid')
        }

        // Log team statistics if available
        if (Array.isArray(gameData.teamStats) && gameData.teamStats.length > 0) {
          console.log('Team Statistics:', {
            teams: gameData.teamStats.reduce((acc, team) => {
              if (team?.officialId) {
                acc[team.officialId] = team
              }
              return acc
            }, {} as Record<string, TeamGameStats>),
          })
        } else {
          console.warn('Team statistics not available or invalid')
        }

        // Log game state
        const gameState: Partial<{
          scoreboard: GameEventUpdate['scoreboard']
          recentPlays: PlayLog[]
        }> = {}

        if (validateScoreboard(gameData.scoreboard)) {
          gameState.scoreboard = gameData.scoreboard
        }

        if (Array.isArray(gameData.playLogs)) {
          gameState.recentPlays = gameData.playLogs.slice(-5)
        }

        if (Object.keys(gameState).length > 0) {
          console.log('Game State:', gameState)
        }
      } catch (err) {
        console.error('Error processing SSE data:', err)
      }
    }
  }, [data])

  const renderScoreboard = (gameData: NonNullable<SSEData['data']>) => {
    if (typeof gameData === 'string' || !validateScoreboard(gameData.scoreboard))
      return null

    const { scoreboard } = gameData
    return (
      <ScoreBoard>
        <div>
          <h3>Home</h3>
          <p>{scoreboard.homeScore}</p>
          <p>Period Scores: {scoreboard.homePeriodScores?.join(', ') ?? 'N/A'}</p>
        </div>
        <div>
          <h3>Away</h3>
          <p>{scoreboard.awayScore}</p>
          <p>Period Scores: {scoreboard.awayPeriodScores?.join(', ') ?? 'N/A'}</p>
        </div>
        <div>
          <h3>Time</h3>
          <p>Period: {scoreboard.period}</p>
          <p>
            {scoreboard.minutes}:{scoreboard.seconds?.toString().padStart(2, '0')}
          </p>
        </div>
      </ScoreBoard>
    )
  }

  const renderPlayLogs = (gameData: NonNullable<SSEData['data']>) => {
    if (typeof gameData === 'string' || !Array.isArray(gameData.playLogs)) return null

    return gameData.playLogs.slice(-5).map((log: PlayLog) => (
      <PlayLogItem key={log.markerId}>
        <p>
          <strong>
            {log.period}Q {log.minutes}:{log.seconds.toString().padStart(2, '0')}
          </strong>
          {' - '}
          {log.description}
        </p>
      </PlayLogItem>
    ))
  }

  return (
    <Container>
      <Header>
        <h2>Live Game Feed</h2>
        <Controls>
          <Button onClick={connect} disabled={isConnected}>
            {isConnected ? 'Connected' : 'Connect'}
          </Button>
          <Button onClick={disconnect} disabled={!isConnected}>
            Disconnect
          </Button>
        </Controls>
      </Header>

      {error && (
        <div style={{ color: 'red', marginBottom: '20px' }}>Error: {error.message}</div>
      )}

      {isConnected ? (
        data?.type === 'eventUpdate' && typeof data.data !== 'string' ? (
          <>
            {renderScoreboard(data.data)}
            <h3>Recent Plays</h3>
            {renderPlayLogs(data.data)}
          </>
        ) : (
          <p>Waiting for game updates...</p>
        )
      ) : (
        <p>Disconnected. Click "Connect" to start receiving updates.</p>
      )}
    </Container>
  )
}

export default SSEComponent
